import { BreadcrumbItem } from '@goed-platform/breadcrumb/type';
import { Breadcrumbs } from '@goed-platform/breadcrumb/ui';
import { ImageLayoutEnum, ImageObjectfitEnum } from '@goed-platform/product/enums';
import { Config, nextI18nextConfig } from '@goed-platform/shared/constants';
import { CustomAppProps, RoutesEnum } from '@goed-platform/shared/types';
import { BaseLayout, OptimizedImage, SEOTags } from '@goed-platform/shared/ui';
import { SVGCalendar, SVGChevronRight } from '@goed-platform/shared/utils';
import { SymfonyDataAccess } from '@goed-platform/symfony/data-access';
import { AppointmentType, InfoHubExpert } from '@goed-platform/symfony/types';
import exp from 'constants';
import { GetStaticProps } from 'next';
import { UserConfig, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FC } from 'react';

type PageProps = CustomAppProps & {
    experts: InfoHubExpert[];
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
    const data = await SymfonyDataAccess.getInstance({ locale: locale }).getInfoHubExperts();

    if (!Array.isArray(data)) {
        return {
            redirect: {
                destination: RoutesEnum.error404,
            },
            props: {},
        };
    }

    return {
        props: {
            experts: data,
            ...(await serverSideTranslations(locale, ['common'], nextI18nextConfig as UserConfig)),
        },
        revalidate: Config.global.revalidation,
    };
};

const AppointOverview: FC<PageProps> = ({ experts = [], navigation }: PageProps): JSX.Element => {
    const { t } = useTranslation();
    const router = useRouter();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            label: t('appoint.overview.title'),
        },
    ];

    const filterExperts = (arr: InfoHubExpert[]) => {
        return arr?.filter((expert) => expert.appointment_types && expert.appointment_types.length > 0);
    };

    const ExpertBlock = ({ name, icon_path, appointment_types, meta }: InfoHubExpert): JSX.Element => (
        <div className="rounded-lg bg-white p-4 p-md-6 h-100 d-flex flex-column">
            <div className="d-flex align-items-center mb-3 mb-md-2">
                <div className="ws-20 hs-20 ws-md-30 hs-md-30 mr-2 mr-md-4 rounded">
                    <OptimizedImage
                        image={{
                            sortOrder: 0,
                            url: icon_path,
                        }}
                        alt={name}
                        layout={ImageLayoutEnum.fill}
                        objectFit={ImageObjectfitEnum.contain}
                        priority
                        imageClassName="rounded"
                    />
                </div>

                <a
                    href={`${Config.sites.main}/${router.locale}/advies/specialisten/${meta.slug ?? ''}`}
                    className="border-0 text-primary font-weight-bold font-size-22 font-size-md-24 line-height-24 line-height-md-28"
                    title={name}
                >
                    {name}
                </a>
            </div>

            <div className="d-flex align-items-center font-size-18 font-weight-bold px-1 mb-3">
                <SVGCalendar className="sprite sprite-md text-neutral mr-2" />
                {t('appoint.overview.expert.cart.appointmentInBranch')}
            </div>

            <div className="list-divider-solid"></div>

            {appointment_types.map((appointmentType, key) => (
                <Link
                    href={
                        appointmentType.appointment_type === 'appointment_external'
                            ? appointmentType.external_url
                            : {
                                  pathname: `${RoutesEnum.appoint}/${appointmentType.appoint_token ?? ''}`,
                                  query: {
                                      ...(appointmentType.widget_id && { widgetId: appointmentType.widget_id }),
                                      ...(appointmentType.appoint_id && { type: appointmentType.appoint_id }),
                                      ...(appointmentType.group_type && { typegroup: appointmentType.group_type }),
                                  },
                              }
                    }
                    key={key}
                >
                    <a className="d-flex align-items-center justify-content-between text-purple font-size-18 font-weight-bold py-2 px-1 border-bottom-1">
                        {appointmentType.title}
                        <SVGChevronRight className="sprite sprite-md text-purple" />
                    </a>
                </Link>
            ))}
            <div className="h-100"></div>
            <a
                href={`${Config.sites.main}/${router.locale}/advies/specialisten/${meta.slug ?? ''}`}
                className="w-100 p-2 text-center rounded-pill border-1 border-purple font-weight-bold align-end mt-6 mt-md-12"
            >
                {t('appoint.overview.expert.cart.cta.help', {
                    name: name,
                })}
            </a>
        </div>
    );

    return (
        <BaseLayout navigation={navigation}>
            <>
                <SEOTags
                    pageTitle={t('appoint.overview.meta.title')}
                    metaDescription={t('appoint.overview.meta.description')}
                />
                <Breadcrumbs homeUrl={Config.sites.main} items={breadcrumbs} />
                <div className="container py-md-6 py-md-12">
                    <div className="row">
                        <div className="col-12 mb-6">
                            <div className="row">
                                <div className="col-12 col-md-10 col-lg-7 mb-8 mb-md-18">
                                    <h1 className="text-primary font-size-36 font-size-md-48 mb-4 mb-md-6">
                                        {t('appoint.overview.title')}
                                    </h1>
                                    <p
                                        className="font-size-20 font-size-md-24 text-neutral-a80 line-height-30 line-height-md-36 mb-0"
                                        dangerouslySetInnerHTML={{ __html: t('appoint.overview.experts.description') }}
                                    ></p>
                                </div>
                            </div>

                            <div className="row">
                                {filterExperts(experts)?.map((expert, key) => (
                                    <div className="col-12 col-md-6 col-xl-4 mb-8" key={key}>
                                        <ExpertBlock {...expert} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </>
        </BaseLayout>
    );
};

export default AppointOverview;
